/**
 * The Controls slot of BA-85 ("The Controls slot: what shows, in order"): the "now" card,
 * the live sent-schedule panel or today's Current Action card, and under it at most one
 * report of a sent schedule that has ended.
 */

import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Action } from '../types/action';
import { isSentScheduleActive } from '../types/sentSchedule';
import { useTabletNow } from '../hooks/useTabletNow';
import { useSchedules } from '../hooks/useSchedules';
import {
  failedReplacedSchedule,
  identityOf,
  readDismissed,
  readReplacedAction,
  replacedByName,
  reportNode,
  storeDismissed,
} from '../utils/sentSchedule';
import { CurrentActionPanel } from './CurrentActionPanel';
import { SentSchedulePanel } from './SentSchedulePanel';
import { SentScheduleReport } from './SentScheduleReport';
import { useSendScheduleFlow } from './useSendScheduleFlow';

interface TabletNowSlotProps {
  deviceId: string;
  deviceName: string;
  canSend: boolean;
  actions: Action[];
  onReloadActions?: () => void;
}

export const TabletNowSlot: React.FC<TabletNowSlotProps> = ({
  deviceId,
  deviceName,
  canSend,
  actions,
  onReloadActions,
}) => {
  const { t } = useTranslation();
  const tablet = useTabletNow(deviceId);
  const { schedules } = useSchedules(deviceId);
  const { sentSchedule, previousSentSchedule, currentAction } = tablet;
  const actionName = currentAction
    ? actions.find((a) => a.id === currentAction.actionId)?.name ?? currentAction.actionId
    : '';
  const [dismissed, setDismissed] = useState<string | null>(() => readDismissed(deviceId));
  useEffect(() => setDismissed(readDismissed(deviceId)), [deviceId]);

  const flow = useSendScheduleFlow({
    deviceId,
    deviceName,
    sentSchedule,
    currentAction,
    actionName,
    tabletOffline: tablet.tabletOffline,
  });

  if (!tablet.ready) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2}>
            <CircularProgress size={24} />
            <Typography>{t('currentAction.loadingCurrentAction')}</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const live = sentSchedule && isSentScheduleActive(sentSchedule) ? sentSchedule : null;
  const report = reportNode(sentSchedule, previousSentSchedule, dismissed);
  const reportIdentity = identityOf(report);

  let failedReplaced: { kind: 'schedule' | 'action'; name: string } | null = null;
  if (report?.state === 'failed') {
    const schedule = failedReplacedSchedule(report, report === sentSchedule ? previousSentSchedule : null);
    const action = schedule ? null : readReplacedAction(deviceId, reportIdentity);
    failedReplaced = schedule ? { kind: 'schedule', name: schedule } : action ? { kind: 'action', name: action } : null;
  }
  const error = report ? flow.errors[report.scheduleId] : undefined;

  return (
    <>
      {live ? (
        <SentSchedulePanel
          node={live}
          deviceId={deviceId}
          deviceName={deviceName}
          actions={actions}
          schedules={schedules}
          tabletOffline={tablet.tabletOffline}
          lastSeen={tablet.lastSeen}
          online={tablet.online}
          now={tablet.now}
          onReloadActions={onReloadActions}
        />
      ) : (
        <CurrentActionPanel deviceId={deviceId} actions={actions} onReloadActions={onReloadActions} />
      )}

      {report && (
        <Box sx={{ mt: 3 }}>
          <SentScheduleReport
            node={report}
            deviceId={deviceId}
            actions={actions}
            schedules={schedules}
            replacedBy={replacedByName(report, currentAction, actionName,
              report === previousSentSchedule ? sentSchedule : null)}
            failedReplaced={failedReplaced}
            now={tablet.now}
            onDismiss={() => {
              storeDismissed(deviceId, reportIdentity);
              setDismissed(reportIdentity);
            }}
            onSendAgain={() => flow.request({ scheduleId: report.scheduleId, name: report.schedule?.name ?? '' })}
            sending={flow.sendingId === report.scheduleId}
            sendDisabled={!canSend || !tablet.online || !!flow.sendingId}
            sendError={error ? t(`sentSchedule.${error}`, { name: report.schedule?.name ?? '' }) : undefined}
          />
        </Box>
      )}

      {flow.dialog}
    </>
  );
};
